// Dashboard numbers for Admin and Accounts: KPIs, the "Needs attention" ranking,
// department load and time per department. Pure functions so they can be tested.
import { calendarDaysBetween, formatDuration, parseDay } from './format';
import { orderStatus } from './status';
import type { Department, DepartmentTask, WorkOrder } from './types';

const HOUR_MS = 3_600_000;
export const STUCK_STARTED_HOURS = 24;
export const SLOW_INVOICE_HOURS = 24;

export interface DashboardKpis {
  dueToday: number;
  dueTodayNotStarted: number;
  overdue: number;
  oldestOverdueDays: number;
  inProgress: number;
  toCollect: number;
  toCollectCustomers: number;
}

function live(orders: WorkOrder[]): WorkOrder[] {
  return orders.filter((order) => !order.deletedAt && order.status === 'active');
}

export function dashboardKpis(orders: WorkOrder[], now = new Date()): DashboardKpis {
  const active = live(orders);
  const dueDays = (order: WorkOrder) => calendarDaysBetween(now, parseDay(order.deliveryDate));
  const dueToday = active.filter((order) => dueDays(order) === 0);
  const overdue = active.filter((order) => dueDays(order) < 0);
  const balances = new Map<string, number>();
  for (const order of orders) {
    if (order.customerBalance !== undefined) balances.set(order.clientId, order.customerBalance);
  }
  const owing = [...balances.values()].filter((balance) => balance > 0);
  return {
    dueToday: dueToday.length,
    dueTodayNotStarted: dueToday.filter((order) => !order.tasks.some((task) => task.status !== 'pending')).length,
    overdue: overdue.length,
    oldestOverdueDays: overdue.reduce((max, order) => Math.max(max, -dueDays(order)), 0),
    inProgress: active.filter((order) => order.accountsApproved).length,
    toCollect: owing.reduce((sum, balance) => sum + balance, 0),
    toCollectCustomers: owing.length,
  };
}

export type AttentionReason = 'late' | 'invoice' | 'stuck' | 'ready';

export interface AttentionItem {
  order: WorkOrder;
  reason: AttentionReason;
  detail: string;
  score: number;
}

/** Ranked by risk: lateness first, then invoices waiting, then work left started, then deliveries ready. */
export function needsAttention(orders: WorkOrder[], now = new Date()): AttentionItem[] {
  const items: AttentionItem[] = [];
  for (const order of live(orders)) {
    const days = calendarDaysBetween(now, parseDay(order.deliveryDate));
    if (days < 0) {
      items.push({ order, reason: 'late', detail: `${-days}d late`, score: 4000 + -days });
      continue;
    }
    if (!order.accountsApproved) {
      const waited = now.getTime() - new Date(order.createdAt).getTime();
      if (waited >= SLOW_INVOICE_HOURS * HOUR_MS) {
        items.push({ order, reason: 'invoice', detail: `Awaiting invoice ${formatDuration(waited)}`, score: 3000 + waited / HOUR_MS });
      }
      continue;
    }
    const stuck = order.tasks
      .filter((task) => task.status === 'started' && task.startedAt)
      .map((task) => ({ task, age: now.getTime() - new Date(task.startedAt!).getTime() }))
      .filter(({ age }) => age >= STUCK_STARTED_HOURS * HOUR_MS)
      .sort((left, right) => right.age - left.age)[0];
    if (stuck) {
      const who = stuck.task.pickedUpByName ? ` · ${stuck.task.pickedUpByName}` : '';
      items.push({
        order,
        reason: 'stuck',
        detail: `${stuck.task.departmentName} started ${formatDuration(stuck.age)} ago${who}`,
        score: 2000 + stuck.age / HOUR_MS,
      });
      continue;
    }
    if (orderStatus(order).tone === 'ready') {
      items.push({ order, reason: 'ready', detail: 'Ready for delivery', score: 1000 - Math.max(days, 0) });
    }
  }
  return items.sort((left, right) => right.score - left.score);
}

export interface DepartmentLoad {
  department: Department;
  pending: number;
  started: number;
}

export function departmentLoad(tasks: DepartmentTask[], departments: Department[], orders: WorkOrder[]): DepartmentLoad[] {
  const liveOrderIds = new Set(live(orders).map((order) => order.id));
  return departments
    .filter((department) => department.active && department.queueEnabled)
    .map((department) => {
      const open = tasks.filter((task) =>
        task.departmentId === department.id
        && task.availableAt
        && liveOrderIds.has(task.workOrderId));
      return {
        department,
        pending: open.filter((task) => task.status === 'pending').length,
        started: open.filter((task) => task.status === 'started').length,
      };
    });
}

export interface DepartmentTiming {
  department: Department;
  completed: number;
  averageWaitMs: number | null;
  averageWorkMs: number | null;
}

/** Average wait (available → started) and work (started → finished) over the last `days` days. */
export function departmentTimings(
  tasks: DepartmentTask[],
  departments: Department[],
  now = new Date(),
  days = 30,
): DepartmentTiming[] {
  const since = now.getTime() - days * 24 * HOUR_MS;
  const average = (values: number[]) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
  return departments
    .filter((department) => department.active && department.queueEnabled)
    .map((department) => {
      const done = tasks.filter((task) =>
        task.departmentId === department.id
        && task.status === 'completed'
        && task.finishedAt
        && new Date(task.finishedAt).getTime() >= since);
      const waits = done
        .filter((task) => task.availableAt && task.startedAt)
        .map((task) => new Date(task.startedAt!).getTime() - new Date(task.availableAt!).getTime())
        .filter((value) => value >= 0);
      const work = done
        .filter((task) => task.startedAt && task.finishedAt)
        .map((task) => new Date(task.finishedAt!).getTime() - new Date(task.startedAt!).getTime())
        .filter((value) => value >= 0);
      return { department, completed: done.length, averageWaitMs: average(waits), averageWorkMs: average(work) };
    });
}
